<template>
  <section class="teacher-week-page">
    <Navbar />
    <section class="hero fixed-nav responsive-pad">
      <div class="hero-head pt-4 pb-4">
        <p class="title">
          Lehrer-Stundenplan
        </p>
      </div>

      <b-field :label="$t('teacher-management.search')">
        <b-autocomplete
          v-model="name"
          :placeholder="$t('teacher-management.search-placeholder')"
          :keep-first="true"
          :open-on-focus="true"
          :data="search_teachers"
          field="fullName"
          :empty="[]"
          :clearable="true"
          @input="search"
          @select="selectTeacher">
          <template #empty>Keine Ergebnisse</template>
        </b-autocomplete>
      </b-field>

      <div v-if="editableTeacher" class="hero-body p-0 pt-5">
        <div class="teacher-meta">
          <div class="teacher-meta-row">
            <strong>Fächer:</strong>
            <span>{{ formatList(editableTeacher.subjects, 'name') }}</span>
          </div>
          <div class="teacher-meta-row">
            <strong>Klassen:</strong>
            <span>{{ formatList(editableTeacher.classes) }}</span>
          </div>
          <div class="teacher-meta-row">
            <strong>Bereitschaften:</strong>
            <span>{{ formatStandbyHours(editableTeacher.standby_hours) }}</span>
          </div>
          <div class="teacher-meta-row">
            <strong>Bereitschaften diese Woche:</strong>
            <span>{{ formatStandbyHours(editableTeacher.standby_hours_week) }}</span>
          </div>
          <div class="teacher-meta-row">
            <strong>Zusätzliche Bereitschaftsstunden:</strong>
            <b-numberinput v-model="editableTeacher.standby_hours_extra"
              aria-label="Zusätzliche Bereitschaftsstunden"
              :step="0.5" @input="change" />
          </div>
        </div>

        <b-tabs v-model="selectedDay" class="tab-custom">
          <b-tab-item
            v-for="(day, dayIndex) in editableTeacher.week"
            :key="`${editableTeacher.id}-${dayIndex}`"
            :label="day.day">
            <b-table class="app-table"
              :data="day.hours"
              :bordered="false"
              :striped="false"
              :narrowed="false"
              :hoverable="true"
              :loading="false"
              :focusable="false"
              :mobile-cards="true"
              :paginated="false">
              <b-table-column field="name" label="Stunde" v-slot="props">
                <strong>{{ props.row.name }}</strong>
              </b-table-column>
              <b-table-column field="subject" label="Fach" v-slot="props">
                <b-select
                  v-model="props.row.subject"
                  placeholder="Fach auswählen"
                  @input="change">
                  <option value="">
                    -
                  </option>
                  <option v-for="subject in subjectOptions" :key="subject" :value="subject">
                    {{ subject }}
                  </option>
                </b-select>
              </b-table-column>
              <b-table-column field="class" label="Klasse" v-slot="props">
                <b-input v-model="props.row.class" @input="change"></b-input>
              </b-table-column>
              <b-table-column field="room" label="Raum" v-slot="props">
                <b-input v-model="props.row.room" @input="change"></b-input>
              </b-table-column>
            </b-table>
          </b-tab-item>
        </b-tabs>

        <div class="teacher-week-actions">
          <Button
            :disabled="!changed"
            :type="'zoom'"
            @click="save"
            :text="'Änderungen speichern'" />
        </div>
      </div>

      <div v-else class="hero-body p-0 pt-5">
        <div class="notification is-light">
          Bitte wählen Sie einen Lehrer aus, um den Wochenstundenplan zu pflegen.
        </div>
      </div>
    </section>
    <Footer />
  </section>
</template>

<script>
import Navbar from '../components/Common/Navbar.vue';
import Footer from '../components/Common/Footer.vue';
import Button from '../components/Common/Button.vue';

const HOUR_NAMES = ['HU1', 'HU2', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
const DAYS = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag'];
const getNextSchoolDayIndex = () => {
  const today = new Date().getDay();
  return today >= 5 ? 0 : today;
};
const DEFAULT_SUBJECTS = [
  'Architekturfahrt',
  'Begleitung 1-2',
  'Berufskunde',
  'Berufspraktikum',
  'Biologie',
  'Blasorchester',
  'Buchbinden',
  'Chemie',
  'Chor',
  'Darstellende Geometrie',
  'DAZ',
  'Deutsch',
  'Englisch',
  'Erdkunde',
  'Eurythmie',
  'Franzoesisch',
  'Gartenbau',
  'Geschichte',
  'Gitarrenorchester',
  'Handarbeit',
  'Hauptunterricht',
  'Informatik',
  'Inklusion',
  'Klangorchester',
  'Klassenbetreuung',
  'Klettern',
  'Kochen',
  'Korbflechten',
  'Kunst',
  'Landwirtschaftspraktikum',
  'Leier-Unterricht',
  'Leierorchester',
  'Malen',
  'Malen / Zeichnen',
  'Mathematik',
  'Physik',
  'Mathe',
  'Metall',
  'Mittag',
  'Mittag Oberstufe',
  'Musik',
  'Oekologie',
  'Percussion-Orchester',
  'Plastizieren',
  'Politik',
  'Praktikum',
  'Querfloetenensemble',
  'Rechtskunde',
  'Religion',
  'Russisch',
  'Schneidern',
  'Schreinern',
  'Sozialpaedagogik',
  'Spielturnen',
  'Spinnen-Weben',
  'Sport',
  'Sprachprojekt',
  'Sprachstreifen',
  'Steinmetzen',
  'Stillarbeit',
  'Streichorchester',
  'Tagesabschluss',
  'Textiltechnologie',
  'Theater 8',
  'Toepfern',
  'Uebstunde',
  'Verfuegungsstunde',
  'Verfuegungsstunde VK',
  'Verfuegungsstunde_2Kl',
  'Vermessungspraktikum',
  'Vermessungspraktikum 2kl',
  'Werken',
  'Wirtschaft / Politik',
];

export default {
  components: {
    Navbar,
    Footer,
    Button,
  },

  data() {
    return {
      name: '',
      selectedDay: getNextSchoolDayIndex(),
      changed: false,
      editableTeacher: null,
    };
  },

  created() {
    this.$store.dispatch('Teachers/load');
  },

  computed: {
    teachers() {
      return this.$store.getters['Teachers/get_teacher_records'];
    },
    search_teachers() {
      if (this.$store.getters['Teachers/get_search_list'].length === 0) {
        return this.teachers;
      }
      return this.$store.getters['Teachers/get_search_list'];
    },
    subjectOptions() {
      if (!this.editableTeacher || !Array.isArray(this.editableTeacher.subjects)) {
        return DEFAULT_SUBJECTS;
      }

      const teacherSubjects = this.editableTeacher.subjects
        .map((subject) => {
          if (typeof subject === 'string') {
            return subject;
          }
          if (subject && typeof subject.name === 'string') {
            return subject.name;
          }
          return '';
        })
        .filter((subject) => subject !== '');

      return [...new Set([...teacherSubjects, ...DEFAULT_SUBJECTS])];
    },
  },

  methods: {
    createEmptyHours() {
      return HOUR_NAMES.map((name) => ({
        name,
        subject: '',
        class: '',
        room: '',
      }));
    },
    createEmptyWeek() {
      return DAYS.map((day, index) => ({
        index_day: index,
        day,
        hours: this.createEmptyHours(),
      }));
    },
    normalizeWeek(week) {
      const normalized = this.createEmptyWeek();

      if (!Array.isArray(week)) {
        return normalized;
      }

      for (let dayIndex = 0; dayIndex < normalized.length; dayIndex += 1) {
        const sourceDay = week[dayIndex] || {};
        normalized[dayIndex].index_day = sourceDay.index_day !== undefined
          ? sourceDay.index_day
          : dayIndex;
        normalized[dayIndex].day = sourceDay.day || normalized[dayIndex].day;

        if (Array.isArray(sourceDay.hours)) {
          for (let hourIndex = 0; hourIndex < normalized[dayIndex].hours.length; hourIndex += 1) {
            const sourceHour = sourceDay.hours[hourIndex] || {};
            normalized[dayIndex].hours[hourIndex] = {
              name: sourceHour.name || normalized[dayIndex].hours[hourIndex].name,
              subject: sourceHour.subject || '',
              class: sourceHour.class || '',
              room: sourceHour.room || '',
            };
          }
        }
      }

      return normalized;
    },
    search() {
      if (this.name === '') {
        return;
      }
      this.$store.dispatch('Teachers/search_teacher', this.name);
    },
    change() {
      this.changed = true;
    },
    formatList(items, key = null) {
      if (!Array.isArray(items) || items.length === 0) {
        return '-';
      }

      return items
        .map((item) => {
          if (typeof item === 'string') {
            return item;
          }

          if (key && item && typeof item[key] === 'string') {
            return item[key];
          }

          if (item && typeof item.name === 'string') {
            return item.name;
          }

          return '';
        })
        .filter((item) => item !== '')
        .join(', ') || '-';
    },
    formatStandbyHours(standbyHours) {
      if (Array.isArray(standbyHours)) {
        return standbyHours.length > 0 ? `${standbyHours.join(', ')} Stunden` : '-';
      }

      if (standbyHours === undefined || standbyHours === null || standbyHours === '') {
        return '-';
      }

      return `${String(standbyHours)} Stunden`;
    },
    selectTeacher(teacher) {
      const teacherRecord = this.teachers.find((entry) => entry.id === teacher.id);

      if (!teacherRecord) {
        return;
      }

      this.name = teacherRecord.fullName;
      this.selectedDay = getNextSchoolDayIndex();
      this.changed = false;
      this.editableTeacher = {
        id: teacherRecord.id,
        fullName: teacherRecord.fullName,
        subjects: teacherRecord.subjects || [],
        classes: teacherRecord.classes || [],
        standby_hours: teacherRecord.standby_hours,
        standby_hours_week: teacherRecord.standby_hours_week,
        standby_hours_extra: teacherRecord.standby_hours_extra || 0,
        week: this.normalizeWeek(teacherRecord.week),
      };
    },
    save() {
      this.$store.dispatch('Teachers/save_teacher_week', {
        id: this.editableTeacher.id,
        week: this.editableTeacher.week,
        standby_hours_extra: this.editableTeacher.standby_hours_extra,
      }).then(async (response) => {
        if (response) {
          this.$buefy.toast.open({
            message: 'Stundenplan erfolgreich gespeichert',
            type: 'is-success',
          });
          this.changed = false;
          await this.$store.dispatch('Teachers/load');
          const saved = this.teachers.find((teacher) => teacher.id === this.editableTeacher.id);
          if (saved) this.editableTeacher.standby_hours = saved.standby_hours;
        } else {
          this.$buefy.toast.open({
            message: 'Etwas ist schiefgelaufen...',
            type: 'is-danger',
          });
        }
      });
    },
  },
};
</script>

<style lang="scss">
.teacher-week-page {
  min-height: 100vh;

  .teacher-meta {
    margin-bottom: 1.5rem;
    padding: 1rem 1.25rem;
    background-color: #f7f7f7;
    border-radius: 0.5rem;
  }

  .teacher-meta-row + .teacher-meta-row {
    margin-top: 0.4rem;
  }

  .teacher-meta-row strong {
    display: inline-block;
    min-width: 10rem;
  }

  .teacher-week-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 1.5rem;
    padding-bottom: 1rem;
  }

  .table strong {
    display: inline-block;
    min-width: 2.5rem;
  }
}
</style>
